import { useEffect, useState } from 'react'

import {
  useFetchBanksQuery,
  useResolveBankDetailsQuery,
} from '../../redux-toolkits/misc/bank.slice'
import { SettlementAccount } from '../../redux-toolkits/settlements/settlements.type'
import { Vendor } from '../../redux-toolkits/vendors/vendors.types'
import Dropdown from '../inputs/dropdown'
import { EditInput } from '../inputs/editInput'
import { SearchInput } from '../inputs/searchInput'
import { TextInput } from '../inputs/textInput'

export const SettlementAccountDetails = ({
  vendor,
  className,
  loading = false,
  onModify = () => {},
  error,
}: {
  vendor?: Vendor
  className?: string
  loading?: boolean
  onModify?: (data: SettlementAccount) => void
  error?: string
}) => {
  const [isEdit, setIsEdit] = useState(false)

  const [payload, setPayload] = useState<SettlementAccount>({
    accountNumber: vendor?.settlementAccount?.accountNumber ?? '',
    accountName: vendor?.settlementAccount?.accountName ?? '',
    bankName: vendor?.settlementAccount?.bankName ?? '',
    bankCode: vendor?.settlementAccount?.bankCode ?? '',
  })

  const { data: banks, isFetching: fetchingBanks } =
    useFetchBanksQuery(undefined)

  const {
    data: accountDetails,
    isFetching: resolvingAccount,
    refetch: resolveAccount,
    isError: resolveAccountError,
    isSuccess: resolveAccountSuccess,
  } = useResolveBankDetailsQuery(
    {
      accountNumber: payload?.accountNumber,
      bankCode: payload?.bankCode,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !payload?.accountNumber || !payload?.bankCode,
    },
  )

  const resetPayload = () => {
    setPayload({
      accountNumber: vendor?.settlementAccount?.accountNumber ?? '',
      accountName: vendor?.settlementAccount?.accountName ?? '',
      bankName: vendor?.settlementAccount?.bankName ?? '',
      bankCode: vendor?.settlementAccount?.bankCode ?? '',
    })
  }

  useEffect(() => {
    if (accountDetails?.data && payload) {
      setPayload({
        ...payload,
        accountName: accountDetails.data.account_name,
      })
    }
  }, [accountDetails])

  return payload ? (
    <EditInput
      title="Settlement Account"
      value={``}
      editing={isEdit}
      onEditSave={() => {
        onModify(payload)
        setIsEdit(false)
      }}
      onEditClick={() => {
        setIsEdit(true)
      }}
      onCancel={() => {
        setIsEdit(false)
        resetPayload()
      }}
      loading={loading || resolvingAccount}
      disabled={
        loading ||
        resolvingAccount ||
        resolveAccountError ||
        !resolveAccountSuccess
      }
      className={className}
      errors={error}
    >
      {({ isEditing }) => {
        return !isEditing ? (
          <>
            {payload.accountNumber ? (
              <div className="text-sec-black flex gap-2 items-center">
                <p>{`${payload.accountNumber}`}</p>

                <p>{`• ${payload.bankName}`}</p>

                <p>{`• ${payload.accountName}`}</p>
              </div>
            ) : (
              'No settlement details provided'
            )}
          </>
        ) : (
          <div className="flex flex-col gap-4 max-w-[800px]">
            <SearchInput
              hasIcon={false}
              useDefaultTextAppearance
              placeholder="Account Number"
              searchFunction={(search) => {
                setPayload({
                  ...payload,
                  accountNumber: search,
                })
              }}
              initialValue={payload.accountNumber}
            />

            <div className="w-full flex flex-col gap-2">
              <Dropdown
                loading={fetchingBanks}
                menuClassName="!max-h-[300px]"
                menuButton={
                  <TextInput
                    hasIcon={false}
                    value={payload.bankName}
                    readOnly
                    placeholder="Choose Bank"
                    className="!min-w-full"
                  />
                }
                menuItems={banks?.data.map((bank) => {
                  return {
                    name: bank.name,
                    value: bank.code,
                    onClick: () => {
                      setPayload({
                        ...payload,
                        bankName: bank.name as string,
                        bankCode: bank.code as string,
                      })
                    },
                  }
                })}
                className="w-full"
              />

              <p
                className={`${
                  resolveAccountError ? 'text-danger' : 'text-sec-black'
                }`}
              >
                {resolvingAccount && 'Resolving bank details...'}
                {!resolvingAccount && resolveAccountError
                  ? 'Could not resolve bank details...'
                  : undefined}
                {!resolvingAccount && !resolveAccountError
                  ? payload?.accountName
                  : undefined}
              </p>
            </div>
          </div>
        )
      }}
    </EditInput>
  ) : undefined
}
